import { UserOverview } from '../../../types/user';
import { WorkpaperCertificationTask } from '../../../types/workpaper';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { formatDateTime } from '../../../utilities/formatting';
import { ButtonV2, TooltipV2 } from '@cimplico/components';
import { XMarkIcon } from '@heroicons/react/20/solid';
import UserAvatar from '../../../components/avatar/UserAvatar';

type CertificationStatus = 'CERTIFIED' | 'AWAITING' | 'NOT_READY';

type CertificationStepProps = {
    title: string;
    status: CertificationStatus;
    tasks?: WorkpaperCertificationTask[];
    certifiedBy?: UserOverview | null;
    certifiedAt?: Date | null;
    canRevokeCertification?: boolean;
    disableCertifyHint?: string;
    onCertify: () => void;
    onRevokeCertification: () => void;
};

const mappings: { [key: string]: string } = {
    PREPARER_PROCEDURES_COMPLETE: 'All preparer procedures have been checked off.',
    PREPARER_WORKSHEETS_COMPLETE: 'All worksheets have been prepared.',
    REVIEWER_PROCEDURES_COMPLETE: 'All reviewer procedures have been checked off.',
    REVIEWER_WORKSHEETS_COMPLETE: 'All worksheets have been reviewed.',
    REVIEWER_OTHER_SCHEDULES_COMPLETE: 'All other schedules have been reviewed.',
    REVIEWER_JOURNALS_COMPLETE: 'All draft journals have been posted.',
    REVIEWER_INTERNAL_COMMENTS_COMPLETE: 'All internal comments have been resolved.',
    REVIEWER_CLIENT_QUERIES_COMPLETE: 'All client queries have been resolved.',
    PREPARER_CERTIFIED: 'Preparer has certified.',
    APPROVER_PROCEDURES_COMPLETE: 'All approver procedures have been checked off.',
    APPROVER_FILES_CHECKED_IN: 'All checked out files have been checked in.',
    REVIEWER_CERTIFIED: 'Reviewer has certified.',
};

function CertificationStep({
    title,
    status,
    tasks = [],
    certifiedBy,
    certifiedAt,
    canRevokeCertification,
    disableCertifyHint,
    onCertify,
    onRevokeCertification,
}: CertificationStepProps): JSX.Element {
    return (
        <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between">
            <div className="flex items-start gap-2">
                <div>
                    {status === 'CERTIFIED' && <CheckCircleIcon className="h-5 w-5 text-green-600" />}
                    {status === 'AWAITING' && <ExclamationCircleIcon className="h-5 w-5 text-yellow-600" />}
                    {status === 'NOT_READY' && <XCircleIcon className="h-5 w-5 text-red-600" />}
                </div>
                <div>
                    <p className="text-left leading-4">{title}</p>
                    <ul className="mt-2 text-left">
                        {tasks.map((task) => {
                            return (
                                <li key={task.task} className="flex items-center mt-2">
                                    {task.complete ? (
                                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <XCircleIcon className="h-4 w-4 text-red-600" />
                                    )}

                                    <p className="ml-1 text-sm">{mappings[task.task]}</p>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
            {certifiedBy && certifiedAt && (
                <div className="ml-8">
                    <div className="flex gap-2 items-center md:items-start">
                        <UserAvatar size="xs" user={certifiedBy} />
                        <div className="flex md:flex-col gap-2 md:gap-0 items-start">
                            <p className="text-sm">
                                {certifiedBy.firstName} {certifiedBy.lastName}
                            </p>
                            <label className="text-sm">{formatDateTime(new Date(certifiedAt))}</label>
                        </div>
                        {canRevokeCertification ? (
                            <TooltipV2 message="Revoke certification">
                                <XMarkIcon className="w-3 h-3 text-gray-500 cursor-pointer" onClick={onRevokeCertification} />
                            </TooltipV2>
                        ) : (
                            <div className="w-3 h-3" />
                        )}
                    </div>
                </div>
            )}
            {!certifiedBy && !certifiedAt && (
                <div className="ml-8 flex grow items-start">
                    <ButtonV2
                        label="Certify"
                        size="xs"
                        onClick={onCertify}
                        isDisabled={!!disableCertifyHint}
                        disabledHint={disableCertifyHint}
                    />
                </div>
            )}
        </div>
    );
}

export default CertificationStep;
